import { useState, useEffect, Fragment } from 'react';
import { api } from '../lib/api';

interface User {
  id: string; email: string; role: string; status: string;
  tokenBalance: number; emailVerified: boolean; createdAt: string;
}

interface UsageStat {
  userId: string; model: string; totalTokens: number; totalCost: string; requestCount: number;
}

interface UserUsage {
  totalTokens: number; totalCost: number; totalRequests: number;
  byModel: { model: string; tokens: number; cost: number; requests: number }[];
}

interface TokenPricing {
  activeModel: string;
  blendedCostPer1M: number | null;
  inputCostPer1M: number | null;
  outputCostPer1M: number | null;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [usageMap, setUsageMap] = useState<Record<string, UserUsage>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [grantTarget, setGrantTarget] = useState<string | null>(null);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [tokenPricing, setTokenPricing] = useState<TokenPricing | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [userData, usageData, pricing] = await Promise.all([
        api.getUsers({ email: search || undefined, status: statusFilter || undefined }),
        api.getUserUsageStats(),
        api.getTokenPricing(),
      ]);
      setUsers(userData.users as User[]);
      setTokenPricing(pricing);

      // Aggregate usage stats per user
      const map: Record<string, UserUsage> = {};
      for (const stat of usageData.stats as UsageStat[]) {
        if (!map[stat.userId]) {
          map[stat.userId] = { totalTokens: 0, totalCost: 0, totalRequests: 0, byModel: [] };
        }
        map[stat.userId].totalTokens += stat.totalTokens;
        map[stat.userId].totalCost += parseFloat(stat.totalCost || '0');
        map[stat.userId].totalRequests += stat.requestCount;
        map[stat.userId].byModel.push({
          model: stat.model,
          tokens: stat.totalTokens,
          cost: parseFloat(stat.totalCost || '0'),
          requests: stat.requestCount,
        });
      }
      setUsageMap(map);
    } catch (e) {
      setMessage(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [search, statusFilter]);

  async function handleGrant() {
    if (!grantTarget || !grantAmount) return;
    try {
      const res = await api.grantTokens(grantTarget, parseInt(grantAmount), grantNote || undefined);
      setMessage(`Tokens adjusted. New balance: ${res.newBalance}`);
      setGrantTarget(null); setGrantAmount(''); setGrantNote('');
      load();
    } catch (e) { setMessage(String(e)); }
  }

  async function handleStatusToggle(user: User) {
    const next = user.status === 'active' ? 'suspended' : 'active';
    try {
      await api.setUserStatus(user.id, next);
      setMessage(`User ${next}`);
      load();
    } catch (e) { setMessage(String(e)); }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Users</h1>
      {message && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded">{message}</div>}

      <div className="flex gap-3 mb-4">
        <input
          className="border rounded px-3 py-2 flex-1"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="border rounded px-3 py-2"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending_deletion">Pending deletion</option>
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Tokens</th>
                <th className="px-4 py-2">Used</th>
                <th className="px-4 py-2">Cost</th>
                <th className="px-4 py-2">Requests</th>
                <th className="px-4 py-2">Verified</th>
                <th className="px-4 py-2">Joined</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const usage = usageMap[u.id];
                return (
                <Fragment key={u.id}><tr className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      u.status === 'active' ? 'bg-green-100 text-green-700'
                      : u.status === 'suspended' ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{u.tokenBalance.toLocaleString()}</td>
                  <td className="px-4 py-2 tabular-nums text-xs text-gray-600">{usage ? usage.totalTokens.toLocaleString() : '—'}</td>
                  <td className="px-4 py-2 tabular-nums text-xs text-gray-600">
                    {usage ? (
                      <button
                        className="underline decoration-dotted hover:text-indigo-600"
                        onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                        title="Click to see per-model breakdown"
                      >
                        ${usage.totalCost.toFixed(4)}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-xs text-gray-600">{usage ? usage.totalRequests.toLocaleString() : '—'}</td>
                  <td className="px-4 py-2">{u.emailVerified ? '✓' : '✗'}</td>
                  <td className="px-4 py-2 text-gray-500">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 flex gap-2">
                    <button
                      className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      onClick={() => setGrantTarget(u.id)}
                    >
                      Tokens
                    </button>
                    <button
                      className={`text-xs px-2 py-1 rounded ${u.status === 'active' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                      onClick={() => handleStatusToggle(u)}
                    >
                      {u.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                  </td>
                </tr>
                {expandedUser === u.id && usage && usage.byModel.length > 0 && (
                  <tr className="bg-gray-50">
                    <td colSpan={10} className="px-4 py-3">
                      <div className="text-xs font-medium text-gray-500 mb-2">Cost breakdown by model</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-400">
                            <th className="text-left py-1 pr-4">Model</th>
                            <th className="text-right py-1 pr-4">Tokens</th>
                            <th className="text-right py-1 pr-4">Cost</th>
                            <th className="text-right py-1">Requests</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usage.byModel.sort((a, b) => b.cost - a.cost).map((m) => (
                            <tr key={m.model} className="text-gray-600">
                              <td className="py-0.5 pr-4 font-mono">{m.model}</td>
                              <td className="py-0.5 pr-4 text-right tabular-nums">{m.tokens.toLocaleString()}</td>
                              <td className="py-0.5 pr-4 text-right tabular-nums">${m.cost.toFixed(6)}</td>
                              <td className="py-0.5 text-right tabular-nums">{m.requests}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Grant tokens modal */}
      {grantTarget && (() => {
        const targetUser = users.find((u) => u.id === grantTarget);
        const targetUsage = usageMap[grantTarget];
        const parsed = parseInt(grantAmount, 10);
        const validAmount = !isNaN(parsed) && parsed !== 0;
        const usdValue = (validAmount && tokenPricing?.blendedCostPer1M != null)
          ? (Math.abs(parsed) / 1_000_000) * tokenPricing.blendedCostPer1M
          : null;

        const PRESETS = [10_000, 50_000, 100_000, 500_000, 1_000_000];

        function presetLabel(n: number) {
          const label = n >= 1_000_000 ? `${n / 1_000_000}M` : `${n / 1_000}k`;
          if (tokenPricing?.blendedCostPer1M == null) return label;
          const usd = (n / 1_000_000) * tokenPricing.blendedCostPer1M;
          return `${label} ≈ $${usd < 0.01 ? usd.toFixed(4) : usd.toFixed(2)}`;
        }

        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-lg font-semibold mb-1">Grant / adjust tokens</h2>
              {targetUser && (
                <div className="text-xs text-gray-500 mb-3 space-y-0.5">
                  <div>{targetUser.email}</div>
                  <div>Balance: <span className="font-medium text-gray-700">{targetUser.tokenBalance.toLocaleString()}</span></div>
                  {targetUsage && (
                    <div>Used total: <span className="font-medium text-gray-700">{targetUsage.totalTokens.toLocaleString()}</span> (${targetUsage.totalCost.toFixed(4)})</div>
                  )}
                  {tokenPricing && (
                    <div>Active model: <span className="font-mono text-gray-700">{tokenPricing.activeModel}</span>
                      {tokenPricing.inputCostPer1M != null && (
                        <span className="ml-1 text-gray-400">(in ${tokenPricing.inputCostPer1M} / out ${tokenPricing.outputCostPer1M} per 1M)</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {PRESETS.map((n) => (
                  <button
                    key={n}
                    className={`text-xs px-2 py-1 rounded border ${grantAmount === String(n) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}`}
                    onClick={() => setGrantAmount(String(n))}
                  >
                    {presetLabel(n)}
                  </button>
                ))}
              </div>

              <input
                type="number"
                className="border rounded px-3 py-2 w-full mb-1"
                placeholder="Amount (negative to deduct)"
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
              />
              {validAmount && usdValue !== null && (
                <div className={`text-xs mb-3 ${parsed < 0 ? 'text-red-500' : 'text-green-600'}`}>
                  {parsed > 0 ? '+ ' : '− '}
                  {Math.abs(parsed).toLocaleString()} tokens ≈ ${usdValue < 0.01 ? usdValue.toFixed(4) : usdValue.toFixed(2)} USD
                </div>
              )}
              {(!validAmount || usdValue === null) && <div className="mb-3" />}

              <input
                className="border rounded px-3 py-2 w-full mb-4"
                placeholder="Note (optional)"
                value={grantNote}
                onChange={(e) => setGrantNote(e.target.value)}
              />
              <div className="flex gap-3">
                <button
                  className="flex-1 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40"
                  disabled={!validAmount}
                  onClick={handleGrant}
                >
                  Confirm
                </button>
                <button className="flex-1 py-2 bg-gray-100 rounded hover:bg-gray-200" onClick={() => { setGrantTarget(null); setGrantAmount(''); setGrantNote(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
